import React, {useCallback, useMemo} from 'react';
import {Stack, TooltipHost, useTheme, Text} from '@fluentui/react';
import {getListClassNames} from 'styles';
import {KeywordStatus, VexWorkbenchItemSimplified} from 'types';
import {useTranslation} from 'react-i18next';
import {ArrayValues, StatusField} from 'components/EntityList/ColumnRenderers';
import {DateTimeFormat, formatDateTime} from 'i18n/localeDateFormat';
import i18n from 'i18n/i18n';

interface VexWorkbenchItemCellProps {
	keywordStatus: KeywordStatus;
	proportionTable: number[];
	halfHeight: boolean;
	item?: VexWorkbenchItemSimplified;
	columnKey?: string;
}

export const VexWorkbenchItemCell: React.FC<VexWorkbenchItemCellProps> = ({
	keywordStatus,
	proportionTable,
	halfHeight,
	item,
	columnKey,
}) => {
	const theme = useTheme();
	const classNames = getListClassNames(theme, halfHeight);

	const {t} = useTranslation('features/dashboard', {
		keyPrefix: 'VexWorkbenchItems',
	});

	const isOverDueDate = useCallback((item: VexWorkbenchItemSimplified) => {
		const {dueDate} = item;
		return new Date() > new Date(dueDate);
	}, []);

	const itemClasses = useMemo(
		() =>
			item && isOverDueDate(item)
				? `${classNames.itemCellPadding0unfocused} ${classNames.itemCellError}`
				: classNames.itemCellPadding0unfocused,
		[classNames, isOverDueDate, item],
	);

	const commonFieldStyle = useMemo(() => {
		return {
			textAlign: 'center' as const,
		};
	}, []);

	const getSimpleField = useCallback(
		(content: any, index: number) => {
			return (
				<div
					style={{
						...commonFieldStyle,
						width: `${proportionTable[index]}%`,
					}}
				>
					{content}
				</div>
			);
		},
		[proportionTable, commonFieldStyle],
	);

	const shrinkNames = useCallback((names: string[]) => {
		return names.map((name, index) => {
			if (name.length > 16) {
				return (
					<TooltipHost content={name} key={`${name}-${index}`}>
						<div>{`${name.substring(0, 13)}...`}</div>
					</TooltipHost>
				);
			}
			return <div key={`${name}-${index}`}>{`${name}`}</div>;
		});
	}, []);

	const shrunkMarkets = useMemo(() => {
		return shrinkNames(item?.marketNames || []);
	}, [item, shrinkNames]);

	const vexClusterNames = useMemo(() => {
		return item?.vexClusterData?.map(vcd => vcd.vexClusterName) || [];
	}, [item]);

	const shrunkVexClusterNames = useMemo(
		() => shrinkNames(vexClusterNames),
		[vexClusterNames, shrinkNames],
	);

	const statusField = useMemo(() => {
		if (!item) return <></>;
		const status = item.regulatoryDocumentStatus || '';
		return status ? (
			<StatusField
				item={item}
				enumName='RegulatoryDocumentStatus'
				value={status}
			/>
		) : (
			<></>
		);
	}, [item]);

	const displayDueDate = useCallback((item: VexWorkbenchItemSimplified) => {
		return item.dueDate
			? formatDateTime(
					new Date(item.dueDate),
					i18n,
					DateTimeFormat.DateMonthWeekday,
			  )
			: '';
	}, []);

	if (!item) {
		return <></>;
	}

	return (
		<div
			key={item.regulatoryDocumentId}
			className={itemClasses}
			data-is-focusable={true}
			style={{textDecoration: 'none'}}
		>
			<Stack
				horizontal
				horizontalAlign={'space-between'}
				styles={{root: {width: '100%', padding: '8px 0'}}}
			>
				<div style={{width: `${proportionTable[0]}%`, padding: '0 8px'}}>
					<a
						href={`/regulations/${item.regulationId}/${
							item.regulatoryDocumentId
						}/paragraphs?filter=${
							keywordStatus === KeywordStatus.Accepted ? 'inProcess' : 'new'
						}`}
					>
						<Text>{item.regulationNumber}</Text>
						<div style={{color: theme.palette.neutralDark}}>
							{item.regulatoryDocumentName} - {statusField}
						</div>
						{columnKey !== t('CompletedTitle') && (
							<div className={classNames.itemSubtext}>
								{`${t('DueDate')}: ${displayDueDate(item)}`}
							</div>
						)}
					</a>
				</div>
				{getSimpleField(<ArrayValues array={shrunkMarkets} limit={4} />, 1)}
				{getSimpleField(
					<ArrayValues array={shrunkVexClusterNames} limit={4} />,
					2,
				)}
				{columnKey !== t('CompletedTitle') && (
					<div style={{textAlign: 'right', width: `${proportionTable[3]}%`}}>
						<ArrayValues
							array={item.vexClusterData.map(vcd => `${vcd.paragraphCount}`)}
							limit={4}
							forceColumnFlow={true}
						/>
					</div>
				)}
			</Stack>
		</div>
	);
};
